<script setup>
import { onMounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { updatePermissions } from '@/libs/utils/permissions.js'
import { formatDate } from '@/libs/utils/date.js'
import { getUserByReference, getUserByReferenceAndVr, searchUsers } from '@/services/userService.js'
import { getDenominationFromUser } from '@/libs/utils/users.js'
import { getCompiledPoll } from '@/services/compiledPollService.js'

import ButtonTornaAllaLista from '@/components/Generics/Buttons/ButtonTornaAllaLista.vue'
import ButtonSalva from '@/components/Generics/Buttons/ButtonSalva.vue'
import ModalSelectDelegati from '@/components/CMB/Common/Modals/ModalSelectDelegati.vue'
import Loading from '@/components/Generics/Loadings/Loading.vue'

import { client } from '@/services/fetch.js'
import { toast } from '@/services/toast.js'
import TranslationProvider from '@/provider/translations.js'

const route = useRoute()
const router = useRouter()
const props = defineProps(['updatePageTitle'])
const isLoading = ref(true)

const compiledPoll = defineModel('compiledPoll')
const dateFrom = defineModel('date-from')
const dateTo = defineModel('date-to')
const dataStipula = defineModel('data-stipula')
const dataInizioPrevista = defineModel('data-inizio-prevista')
const dataConclusionePrevista = defineModel('data-conclusione-prevista')
const selected_CCdelegated = defineModel('selected_CCdelegated')
const selected_DTdelegated = defineModel('selected_DTdelegated')
const note = defineModel('note')

const removed_CCdelegated = ref(null)
const removed_DTdelegated = ref(null)
const userList = ref([])
const currentCapoCommessa = ref(null)
const currentDirettoreTecnico = ref(null)
const modalSelectCC_delegated = ref({
  open: false
})
const modalSelectDT_delegated = ref({
  open: false
})
const loadingSearchUser = ref(false)
const nameSelectedCC = ref('')
const nameSelectedDT = ref('')

const dataValutazione = defineModel('dataValutazione')
const dataApprovazione = defineModel('dataApprovazione')

const utenteDataValutazione = defineModel('utenteDataValutazione')
const utenteDataApprovazione = defineModel('utenteDataApprovazione')

onMounted(async () => {
  try {
    compiledPoll.value = await getCompiledPoll(route.params.id)
  } catch (error) {
    if(error.data.status === 403) {
      router.replace({ path: '/admin/error_403' })
    }

    if(error.data.status === 404) {
      router.replace({ path: '/admin/error_404' })
    }
  }

  let pageTitle = 'Proprietà Questionario - Contratto ' + compiledPoll.value.attributes['gec.uuid']
  props.updatePageTitle(pageTitle)

  dateFrom.value = compiledPoll.value.attributes['gec.date.execution.start_date']
  dateTo.value = compiledPoll.value.attributes['gec.date.execution.conclusion_date']
  dataStipula.value = formatDate(compiledPoll.value.attributes['gec.date.stipulation.date'])
  dataInizioPrevista.value = formatDate(compiledPoll.value.attributes['gec.date.execution.expected_start_date'])
  dataConclusionePrevista.value = formatDate(compiledPoll.value.attributes['gec.date.execution.expected_conclusion_date'])
  note.value = compiledPoll.value.attributes['polls.note'] ?? ''

  dataValutazione.value = formatDate(compiledPoll.value.attributes['polls.to_be_compiled_2_confirmed.evaluated_date'])
  dataApprovazione.value = formatDate(compiledPoll.value.attributes['polls.confirmed_2_approved.approval_date'])
  utenteDataValutazione.value = compiledPoll.value.attributes['polls.to_be_compiled_2_confirmed.evaluated_date.user.denomination']
  utenteDataApprovazione.value = compiledPoll.value.attributes['polls.confirmed_2_approved.approval_date.user.denomination']

  let capiCommessaDelegati = compiledPoll.value.attributes['gec.involved_subject.main_contractor.delegated']?.split('|') ?? []
  selected_CCdelegated.value = capiCommessaDelegati.length === 1 && capiCommessaDelegati[0] === '' ? [] : capiCommessaDelegati

  if (selected_CCdelegated.value[0]) {
    let user = await getUserByReference(selected_CCdelegated.value[0])
    nameSelectedCC.value = getDenominationFromUser(user)
  }

  let direttoriTecniciDelegati = compiledPoll.value.attributes['gec.involved_subject.construction_site_manager.delegated']?.split('|') ?? []
  selected_DTdelegated.value = direttoriTecniciDelegati.length === 1 && direttoriTecniciDelegati[0] === '' ? [] : direttoriTecniciDelegati

  if (selected_DTdelegated.value[0]) {
    let user = await getUserByReference(selected_DTdelegated.value[0])
    nameSelectedDT.value = getDenominationFromUser(user)
  }

  currentCapoCommessa.value = compiledPoll.value.attributes['gec.involved_subject.main_contractor']
  currentDirettoreTecnico.value = compiledPoll.value.attributes['gec.involved_subject.construction_site_manager']

  isLoading.value = false

})

function showPicker(e) {
  e.target.showPicker()
}

function cleanSelectedCC() {
  removed_CCdelegated.value = selected_CCdelegated.value
  selected_CCdelegated.value = []
  nameSelectedCC.value = ''
}

function cleanSelectedDT() {
  removed_DTdelegated.value = selected_DTdelegated.value
  selected_DTdelegated.value = []
  nameSelectedDT.value = ''
}

const getUsers = async (fullSearch = '') => {
  if (fullSearch.length < 3) {
    return
  }

  loadingSearchUser.value = true

  const response = await searchUsers('cmb', {
    fullSearch: fullSearch,
    cc_ref: currentCapoCommessa.value,
    dt_ref: currentDirettoreTecnico.value
  })

  userList.value = response.data
  loadingSearchUser.value = false
}

async function selezionaCapoCommessa(refSelezionato, refRimosso) {
  removed_CCdelegated.value = []
  removed_CCdelegated.value.push(refRimosso)

  selected_CCdelegated.value = []
  selected_CCdelegated.value.push(refSelezionato)

  modalSelectDT_delegated.value.open = false

  let user = await getUserByReferenceAndVr(selected_CCdelegated.value[0], 'cmb')
  nameSelectedCC.value = getDenominationFromUser(user)

  modalSelectCC_delegated.value.open = false
}

async function selezionaDirettoreTecnico(refSelezionato, refRimosso) {
  removed_DTdelegated.value = []
  removed_DTdelegated.value.push(refRimosso)

  selected_DTdelegated.value = []
  selected_DTdelegated.value.push(refSelezionato)

  modalSelectDT_delegated.value.open = false

  let user = await getUserByReferenceAndVr(selected_DTdelegated.value[0], 'cmb')
  nameSelectedDT.value = getDenominationFromUser(user)
}

const submitForm = async () => {
  isLoading.value = true

  try {
    updatePermissions(
      compiledPoll.value,
      currentCapoCommessa.value,
      currentDirettoreTecnico.value,
      selected_CCdelegated.value,
      removed_CCdelegated.value,
      selected_DTdelegated.value,
      removed_DTdelegated.value
    )

    const payload = {
      userReference: compiledPoll.value.userReference,
      campaign: compiledPoll.value.campaign,
      answers: compiledPoll.value.answers,
      pollTemplateRevisionNumber: compiledPoll.value.pollTemplateRevisionNumber,
      permissions: compiledPoll.value.permissions,
      attributes: {
        ...compiledPoll.value.attributes,
        'gec.date.execution.start_date': dateFrom.value,
        'gec.date.execution.conclusion_date': dateTo.value,
        'gec.involved_subject.main_contractor.delegated': selected_CCdelegated.value?.join('|') ?? '',
        'gec.involved_subject.main_contractor.delegated.denomination': nameSelectedCC.value,
        'gec.involved_subject.construction_site_manager.delegated': selected_DTdelegated.value?.join('|') ?? '',
        'gec.involved_subject.construction_site_manager.delegated.denomination': nameSelectedDT.value,
        'polls.note': note.value
      }
    }

    let url = `/compiled-poll/api/compiled-poll/${compiledPoll.value.id}?poll=1`
    await client.put(url, payload)

    removed_CCdelegated.value = null
    removed_DTdelegated.value = null
    isLoading.value = false
    toast.create('Questionario aggiornato', 'success')
  } catch (error) {
    isLoading.value = false
    toast.create('Errore durante il salvataggio. Riprova più tardi.', 'error')
  }
}
</script>

<template>
  <div class="container-fluid px-0">
    <div class="card">
      <div class="card-body scrollable-body">

        <loading v-if="isLoading"></loading>

        <div class="mb-3 container-fluid px-0" v-if="!isLoading">

          <div class="row">

            <div class="col-12 col-lg-6">
              <div class="col-12 mt-3 px-0">
                <label for="data_stipula">
                  {{ TranslationProvider.getTrans('app.data_stipula') }}
                </label>
                <input
                  id="data_stipula"
                  type="date"
                  class="form-control"
                  v-model="dataStipula"
                  disabled
                  readonly
                />
              </div>

              <div class="col-12 mt-3 px-0">
                <label for="data_inizio_prevista">
                  {{ TranslationProvider.getTrans('app.data_di_inizio_prevista') }}
                </label>
                <input
                  id="data_inizio_prevista"
                  type="date"
                  class="form-control"
                  v-model="dataInizioPrevista"
                  disabled
                  readonly
                />
              </div>

              <div class="col-12 mt-3 px-0">
                <label for="data_conclusione_prevista">
                  {{ TranslationProvider.getTrans('app.data_di_conclusione_prevista') }}
                </label>
                <input
                  id="data_conclusione_prevista"
                  type="date"
                  class="form-control"
                  v-model="dataConclusionePrevista"
                  disabled
                  readonly
                />
              </div>
            </div>

            <div class="col-12 col-lg-6">

              <div class="col-12 mt-3 col-placeholder d-none d-lg-block" style="min-height: 55px"></div>

              <div class="col-12 mt-3 px-0">
                <label for="dateFrom">
                  {{ TranslationProvider.getTrans('app.data_effettiva_di_inizio_esecuzione') }}
                </label>
                <input
                  id="dateFrom"
                  type="date"
                  class="form-control"
                  v-model="dateFrom"
                  @click="showPicker"
                  :readonly="!compiledPoll['enabledActions']['settings']"
                  :disabled="!compiledPoll['enabledActions']['settings']"
                />
              </div>
              <div class="col-12 mt-3 px-0">
                <label for="dateFrom">
                  {{ TranslationProvider.getTrans('app.data_effettiva_di_fine_esecuzione') }}
                </label>
                <input
                  id="dateTo"
                  type="date"
                  class="form-control"
                  v-model="dateTo"
                  @click="showPicker"
                  :readonly="!compiledPoll['enabledActions']['settings']"
                  :disabled="!compiledPoll['enabledActions']['settings']"
                />
              </div>
            </div>

            <div class="col-12 col-lg-6 mt-3">
              <label for="selected_CCdel">
                {{ TranslationProvider.getTrans('app.capo_commessa_delegato') }}
              </label>
              <div class="input-group">
                <input type="text" class="form-control"
                       readonly
                       :disabled="!compiledPoll['enabledActions']['editDelegatedCC'] || !compiledPoll['enabledActions']['settings']"
                       :value="nameSelectedCC">
                <button
                  v-if="selected_CCdelegated.length && compiledPoll['enabledActions']['editDelegatedCC'] && compiledPoll['enabledActions']['settings']"
                  class="btn btn-secondary"
                  @click="cleanSelectedCC">
                  {{ TranslationProvider.getTrans('app.remove') }}
                </button>
                <button class="btn btn-primary"
                        v-if="compiledPoll['enabledActions']['editDelegatedCC'] && compiledPoll['enabledActions']['settings']"
                        @click="modalSelectCC_delegated.open = true">
                  {{ TranslationProvider.getTrans('app.seleziona') }}
                </button>
              </div>

              <modal-select-delegati
                v-if="compiledPoll['enabledActions']['editDelegatedCC'] && compiledPoll['enabledActions']['settings']"
                :show="modalSelectCC_delegated.open"
                @close="modalSelectCC_delegated.open = false"
                @search="getUsers"
                :loading="loadingSearchUser"
                :user-list="userList"
                @selected="selezionaCapoCommessa"
                :title="TranslationProvider.getTrans('app.seleziona_capo_commessa_delegato')"
                :sel_delegated="selected_CCdelegated"
                :nome_selezionato="nameSelectedCC"
              ></modal-select-delegati>

            </div>

            <div class="col-12 col-lg-6 mt-3">
              <label for="selected_CCdel">
                {{ TranslationProvider.getTrans('app.direttori_tecnici_delegati') }}
              </label>
              <div class="input-group">
                <input type="text" class="form-control"
                       readonly
                       :disabled="!compiledPoll['enabledActions']['editDelegatedDT'] || !compiledPoll['enabledActions']['settings']"
                       :value="nameSelectedDT">
                <button
                  v-if="selected_DTdelegated.length && compiledPoll['enabledActions']['editDelegatedDT'] && compiledPoll['enabledActions']['settings']"
                  class="btn btn-secondary"
                  @click="cleanSelectedDT">
                  {{ TranslationProvider.getTrans('app.remove') }}
                </button>
                <button class="btn btn-primary"
                        v-if="compiledPoll['enabledActions']['editDelegatedDT'] && compiledPoll['enabledActions']['settings']"
                        @click="modalSelectDT_delegated.open = true">
                  {{ TranslationProvider.getTrans('app.seleziona') }}
                </button>
              </div>

              <modal-select-delegati
                v-if="compiledPoll['enabledActions']['editDelegatedDT'] && compiledPoll['enabledActions']['settings']"
                :show="modalSelectDT_delegated.open"
                @close="modalSelectDT_delegated.open = false"
                @search="getUsers"
                :loading="loadingSearchUser"
                :user-list="userList"
                @selected="selezionaDirettoreTecnico"
                :title="TranslationProvider.getTrans('app.seleziona_direttore_tecnico_delegato')"
                :sel_delegated="selected_DTdelegated"
                :nome_selezionato="nameSelectedDT"
              ></modal-select-delegati>
            </div>

            <div class="col-12 col-lg-12 mt-3">
              <label for="note">
                {{ TranslationProvider.getTrans('app.note') }}
              </label>
              <div class="input-group">
                <textarea class="form-control" id="note"
                          v-model="note"
                          :disabled="!compiledPoll['enabledActions']['settings']"></textarea>
              </div>
            </div>

          </div>

          <div class="row mt-3" v-if="dataValutazione">
            <div class="col-12 col-lg-6 mt-3 mt-lg-0">
              <label for="data_valutazione">
                {{ TranslationProvider.getTrans('app.data_valutazione') }}
              </label>
              <input
                id="data_valutazione"
                type="date"
                class="form-control"
                v-model="dataValutazione"
                disabled
                readonly
              />
            </div>
            <div class="col-12 col-lg-6 mt-3 mt-lg-0">
              <label for="utente_data_valutazione">
                {{ TranslationProvider.getTrans('app.valutato_da') }}
              </label>
              <input
                id="utente_data_valutazione"
                type="text"
                class="form-control"
                v-model="utenteDataValutazione"
                disabled
                readonly
              />
            </div>
          </div>

          <div class="row mt-3" v-if="dataApprovazione">
            <div class="col-12 col-lg-6 mt-3 mt-lg-0">
              <label for="data_valutazione">
                {{ TranslationProvider.getTrans('app.data_approvazione') }}
              </label>
              <input
                id="data_valutazione"
                type="date"
                class="form-control"
                v-model="dataApprovazione"
                disabled
                readonly
              />
            </div>
            <div class="col-12 col-lg-6 mt-3 mt-lg-0">
              <label for="utente_data_valutazione">
                {{ TranslationProvider.getTrans('app.approvato_da') }}
              </label>
              <input
                id="utente_data_valutazione"
                type="text"
                class="form-control"
                v-model="utenteDataApprovazione"
                disabled
                readonly
              />
            </div>
          </div>

          <div class="row mt-3"
               v-if="compiledPoll['attributes']['polls.to_be_compiled_2_wont_do.not_evaluated.reason']">
            <h3>Dati di non valutazione</h3>
            <div class="col-12 col-lg-6 mt-3 mt-lg-0">
              <label for="data_valutazione">Motivazione</label>
              <input
                id="reason"
                type="text"
                class="form-control"
                :value="compiledPoll['attributes']['polls.to_be_compiled_2_wont_do.not_evaluated.reason']"
                disabled
                readonly
              />
            </div>
            <div class="col-12 col-lg-6 mt-3 mt-lg-0">
              <label for="data_valutazione">Note</label>
              <input
                id="reason_note"
                type="text"
                class="form-control"
                :value="compiledPoll['attributes']['polls.to_be_compiled_2_wont_do.not_evaluated.reason.note']"
                disabled
                readonly
              />
            </div>
            <div class="col-12 col-lg-6 mt-3 mt-lg-0">
              <label for="data_valutazione">Data</label>
              <input
                id="date_not_evaluated"
                type="date"
                class="form-control"
                :value="formatDate(compiledPoll['attributes']['polls.to_be_compiled_2_wont_do.not_evaluated_date'])"
                disabled
                readonly
              />
            </div>
            <div class="col-12 col-lg-6 mt-3 mt-lg-0">
              <label for="data_valutazione">Utente</label>
              <input
                id="user_not_evaluated"
                type="text"
                class="form-control"
                :value="compiledPoll['attributes']['polls.to_be_compiled_2_wont_do.not_evaluated_date.user.denomination']"
                disabled
                readonly
              />
            </div>
          </div>

        </div>
      </div>
    </div>

    <div class="justify-content-between d-flex mt-3">
      <router-link
        :to="{ name: 'admin/CMB_CompiledPoll' , query: { page: route.query.page, filters: route.query.filters} }">
        <button-torna-alla-lista></button-torna-alla-lista>
      </router-link>

      <button-salva
        v-if="compiledPoll && compiledPoll['enabledActions']['settings']"
        @click="submitForm"
        :disabled="isLoading"
        :loading="isLoading">
      </button-salva>
    </div>

  </div>
</template>

<style scoped lang="scss">

</style>
